import { render as toPlainText } from "datocms-structured-text-to-plain-text"
import { DateTime } from "luxon"
import { GetStaticProps, NextPage } from "next"
import { useTranslation } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import React, { useMemo } from "react"
import { Image, StructuredText } from "react-datocms"
import styled from "styled-components"
import { truncate } from "@aistihealth/web-common/src/util/strings"

import { ArticleCard } from "../../components/article-card/ArticleCard"
import { Box } from "../../components/Box/Box"
import { Heading } from "../../components/heading/Heading"
import { MainLayout } from "../../components/layouts/main-layout/MainLayout"
import { NextStepsSection } from "../../components/next-steps-section/NextStepsSection"
import { Paragraph } from "../../components/paragraph/Paragraph"
import { Section } from "../../components/section/Section"
import { SingleColLayout } from "../../components/single-col-layout/SingleColLayout"
import { HeroBlock } from "../../datocms/components/HeroBlock"
import { nextLocaleToDatoLocale } from "../../datocms/dato-utils"
import { datoClient } from "../../datocms/gql/client/apollo-client"
import {
  GetBlogIndexPageDocument,
  GetBlogIndexPageQuery,
  GetBlogIndexPageQueryVariables,
  ImgixParamsAuto,
  ImgixParamsFit,
} from "../../datocms/gql/generated/graphql"
import {
  omitParagraphTagRenderRule,
  paragraphRenderRule,
} from "../../datocms/nodeRules"
import { BasePageProps, NEXT_JS_ISR_REVALIDATE } from "../../util/page"
import { theme } from "../../util/styles/theme"

const CONTENT_MAX_LENGTH = 300 // chars

export const getStaticProps: GetStaticProps = async ({ locale, preview }) => {
  const response = await datoClient(preview).query<
    GetBlogIndexPageQuery,
    GetBlogIndexPageQueryVariables
  >({
    query: GetBlogIndexPageDocument,
    variables: {
      imgixParams: {
        auto: [ImgixParamsAuto.format],
        fit: ImgixParamsFit.crop,
        h: 410,
        w: 654,
      },
      locale: nextLocaleToDatoLocale(locale),
    },
  })

  if (!response.data.blogIndexPage) {
    return { notFound: true, revalidate: NEXT_JS_ISR_REVALIDATE }
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, ["common"])),
      blogPosts: response.data.allArticles,
      footer: response.data.footer,
      page: response.data.blogIndexPage,
      titleSuffix: response.data.site.globalSeo.titleSuffix,
    },
    revalidate: NEXT_JS_ISR_REVALIDATE,
  }
}

const ListContainer = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings.xlarge};
`

export type BlogIndexProps = {
  blogPosts: GetBlogIndexPageQuery["allArticles"]
  page: GetBlogIndexPageQuery["blogIndexPage"]
  titleSuffix: string
} & BasePageProps

const BlogIndex: NextPage<BlogIndexProps> = ({
  page,
  titleSuffix,
  blogPosts,
  footer,
}) => {
  const { t } = useTranslation()

  const heroModel = page.hero[0]

  const hero = heroModel && (
    <HeroBlock model={heroModel} sectionCutColor="roseGoldLight" />
  )

  const intro = page.intro[0]
  const paragraphRenderNodeRules = useMemo(() => {
    return [
      paragraphRenderRule({
        size: "large",
      }),
    ]
  }, [])

  return (
    <MainLayout
      type="metaTags"
      topBackground="roseGoldLight"
      bottomBackground="yellow"
      metaTags={page.seoMetaTags}
      titleSuffix={titleSuffix}
      heroComponent={hero}
      footer={footer}
    >
      <Section backgroundColor="roseGoldLight">
        <SingleColLayout
          colProps={{
            xs: 12,
            xsOffset: 0,
            md: 10,
            smOffset: 1,
          }}
        >
          {intro?.header && (
            <Heading as="h2" size="xlarge" align="center">
              <StructuredText
                data={intro.header.value}
                customNodeRules={[omitParagraphTagRenderRule]}
              />
            </Heading>
          )}
          {intro?.text && (
            <StructuredText
              data={intro.text.value}
              customNodeRules={paragraphRenderNodeRules}
            />
          )}
          <ListContainer>
            {blogPosts.length === 0 && (
              <Box>
                <Paragraph>
                  <StructuredText
                    data={page.noBlogPostsMessage.value}
                    customNodeRules={[omitParagraphTagRenderRule]}
                  />
                </Paragraph>
              </Box>
            )}
            {blogPosts.map(p => {
              const image = (
                <Image
                  data={p.image.responsiveImage}
                  style={{ borderRadius: theme.borderRadius.medium }}
                />
              )
              const content = truncate(
                toPlainText(p.content.value),
                CONTENT_MAX_LENGTH,
              )

              return (
                <ArticleCard
                  key={p.id}
                  title={p.title}
                  date={DateTime.fromISO(p.date).toFormat(
                    t("common:formats.date.short"),
                  )}
                  orientation={{ sm: "horizontal", xs: "vertical" }}
                  image={image}
                  content={content}
                  href={`/blog/${encodeURIComponent(p.slug)}`}
                />
              )
            })}
          </ListContainer>
        </SingleColLayout>
      </Section>

      <Section backgroundColor="yellow">
        <NextStepsSection />
      </Section>
    </MainLayout>
  )
}

export default BlogIndex
